'use client';

import React, { useEffect, useRef } from 'react';

const OriginStorySection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = entry.target.querySelectorAll('.reveal-on-scroll');
            reveals.forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="story"
      ref={sectionRef}
      className="relative overflow-hidden bg-charcoal">
      
      {/* ── Act 1: The Grill ── */}
      <div className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="/assets/images/story-hero2.jpg"
            alt="Cuisine asiatique au wok avec flammes"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/60 to-charcoal/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 py-32">
          <div className="max-w-2xl">
            <p className="reveal-on-scroll text-clay text-xs uppercase tracking-widest font-sans font-medium mb-6">
              Notre Cuisine
            </p>
            <h2 className="reveal-on-scroll font-serif italic text-parchment leading-tight mb-8"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
              Saveurs d'Asie.<br />
              <span className="text-clay">En Corse.</span><br />
              Fraîcheur absolue.
            </h2>
            <p className="reveal-on-scroll font-sans text-parchment/60 text-lg leading-relaxed max-w-lg">
              Chaque plat est préparé à la minute dans nos woks traditionnels. Les ingrédients arrivent frais chaque matin de producteurs locaux et asiatiques. La qualité n'est jamais négociée.
            </p>
          </div>
        </div>
      </div>

      {/* ── Act 2: The Hands ── */}
      <div className="relative min-h-screen flex items-center bg-charcoal-mid">
        <div className="max-w-7xl mx-auto px-6 lg:px-16 py-32 grid lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div className="reveal-on-scroll order-2 lg:order-1">
            <p className="text-clay text-xs uppercase tracking-widest font-sans font-medium mb-6">
              L'Équipe
            </p>
            <div className="separator-line mb-8" />
            <h2 className="font-serif italic text-parchment leading-tight mb-8"
            style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
              Une passion<br />
              transmise de<br />
              <em className="text-clay">génération en génération.</em>
            </h2>
            <p className="font-sans text-parchment/60 text-base leading-relaxed mb-6">
              Notre chef a perfectionné son art dans les cuisines de Hanoi, Bangkok et Tokyo avant de s'installer en Corse. Son équipe partage la même passion pour l'authenticité et la qualité. Chaque geste est précis, chaque plat est un hommage.
            </p>
            <p className="font-sans text-parchment/40 text-sm leading-relaxed italic">
              "La cuisine, c'est l'amour que l'on partage à travers les saveurs."<br />
              — Chef Minh, 2020
            </p>
          </div>

          {/* Overlapping images — Template 2 style */}
          <div className="relative h-[520px] order-1 lg:order-2">
            {/* Main image — top right */}
            <div className="absolute top-0 right-0 w-[62%] aspect-[3/4] rounded-4xl overflow-hidden shadow-2xl border border-clay/10 z-10 img-zoom">
              <img
                src="/assets/images/story-team.jpg"
                alt="Chef préparant des plats asiatiques"
                className="w-full h-full object-cover" />

            </div>
            {/* Secondary — left middle */}
            <div className="absolute top-[20%] left-0 w-[52%] aspect-square rounded-4xl overflow-hidden shadow-2xl border border-clay/10 z-20 img-zoom">
              <img
                src="/assets/images/story-recipe.png"
                alt="Recettes traditionnelles asiatiques"
                className="w-full h-full object-cover"
                style={{ filter: 'sepia(0.4) contrast(1.1)' }} />

              {/* Black & white overlay */}
              <div className="absolute inset-0 bg-charcoal/20 mix-blend-multiply" />
            </div>
            {/* Floating label */}
            <div className="absolute bottom-4 right-4 z-30 glass-dark rounded-xl px-4 py-3">
              <p className="text-parchment/50 text-xs font-sans uppercase tracking-widest">Passion</p>
              <p className="text-parchment font-serif italic text-sm">Depuis 2020</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Act 3: The Truck ── */}
      <div className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="/assets/images/story-truck.png"
            alt="Restaurant CorsAsia en Corse avec clients"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-l from-charcoal/85 via-charcoal/50 to-charcoal/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 py-32 flex justify-end">
          <div className="max-w-xl text-right">
            <p className="reveal-on-scroll text-clay text-xs uppercase tracking-widest font-sans font-medium mb-6">
              Aujourd'hui
            </p>
            <h2 className="reveal-on-scroll font-serif italic text-parchment leading-tight mb-8"
            style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
              15 000 clients.<br />
              Une réputation<br />
              <span className="text-clay">bien méritée.</span>
            </h2>
            <p className="reveal-on-scroll font-sans text-parchment/60 text-base leading-relaxed">
              De Ajaccio à Bastia, en passant par Porto-Vecchio — CorsAsia compte aujourd'hui 6 points de vente en Corse. Trois Buffet d'Asie pour un voyage culinaire, et trois Market Traiteur pour emporter les saveurs chez vous. Qualité, fraîcheur et authenticité font notre succès.
            </p>
          </div>
        </div>
      </div>
    </section>);

};

export default OriginStorySection;